import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  UserPlus, 
  Globe, 
  Calendar, 
  Clock, 
  Search, 
  Trash2, 
  X, 
  Edit3,
  Plus,
  Coins,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Shield,
  DollarSign,
  Eye,
  Mail,
  Phone,
  Activity,
  Save,
  UserCheck,
  UserX
} from 'lucide-react';
import "./AdminUserData.css";

import { API_BASE } from "../../../config/api";

const adminApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default function AdminUserData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);

  // Form states
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    fullName: '',
    isAdmin: false,
    isVerified: true
  });

  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    isAdmin: false,
    isVerified: false,
    twoStep: false,
    balances: {
      availableLiquidity: 0,
      lockedInvestment: 0, // ✅ ADDED
      totalDeposited: 0,
      totalWithdrawn: 0,
      netProfitLoss: 0,
      totalInvested: 0,
      currentInvestmentValue: 0
    }
  });

  // Fetch users from database
  useEffect(() => {
    fetchUsers();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchUsers(false);
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchUsers = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    
    try {
      console.log(" Fetching users from database...");
      console.log("API_BASE:", API_BASE);
      
      // ✅ CORRECT ENDPOINT
      const response = await adminApi.get('/api/admin/users');
      
      console.log("✅ Response:", response.data);
      
      if (response.data.success) {
        setUsers(response.data.users || []);
        console.log(`✅ Loaded ${response.data.users?.length || 0} users`);
      }
    } catch (err) {
      console.error("❌ Failed to fetch users:", err);
      console.error("Error response:", err.response?.data);
      setError(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // ===== CREATE USER =====
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!createForm.email || !createForm.password || !createForm.fullName) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    setActionLoading(true);
    
    try {
      // ✅ CORRECT ENDPOINT
      await adminApi.post('/api/admin/users', createForm);
      
      showNotification('✅ User created successfully');
      setShowCreateModal(false);
      setCreateForm({
        email: '',
        password: '',
        fullName: '',
        isAdmin: false,
        isVerified: true
      });
      await fetchUsers(false);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ===== EDIT USER =====
  const handleOpenEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      fullName: user.fullName || '',
      email: user.email || '',
      phone: user.phone || '',
      location: user.location || '',
      isAdmin: user.isAdmin || false,
      isVerified: user.isVerified || false,
      twoStep: user.twoStep || false,
      balances: {
        availableLiquidity: user.balances?.availableLiquidity || 0,
        lockedInvestment: user.balances?.lockedInvestment || 0, // ✅ ADDED
        totalDeposited: user.balances?.totalDeposited || 0,
        totalWithdrawn: user.balances?.totalWithdrawn || 0,
        netProfitLoss: user.balances?.netProfitLoss || 0,
        totalInvested: user.balances?.totalInvested || 0,
        currentInvestmentValue: user.balances?.currentInvestmentValue || 0
      }
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    setActionLoading(true);
    
    try {
      // ✅ CORRECT ENDPOINT
      await adminApi.put(`/api/admin/users/${selectedUser._id}`, editForm);
      
      showNotification('✅ User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      await fetchUsers(false);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to update user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ===== DELETE USER =====
  const handleDeleteUser = async (id) => {
    if (!window.confirm(`⚠️ WARNING: Are you sure you want to delete this user? This action cannot be undone!`)) {
      return;
    }

    setActionLoading(true);
    
    try {
      // ✅ CORRECT ENDPOINT
      await adminApi.delete(`/api/admin/users/${id}`);
      
      showNotification('✅ User deleted successfully');
      await fetchUsers(false);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to delete user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ===== TOGGLE VERIFICATION =====
  const handleToggleVerification = async (userId, currentStatus) => {
    const action = currentStatus ? 'unverify' : 'verify';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    setActionLoading(true);
    
    try {
      // ✅ CORRECT ENDPOINT
      await adminApi.patch(`/api/admin/users/${userId}/status`, {
        isVerified: !currentStatus
      });

      showNotification(`✅ User ${action}ed successfully`);
      await fetchUsers(false);
    } catch (err) {
      showNotification(err.response?.data?.message || `Failed to ${action} user`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const filteredUsers = users.filter(u => 
    u.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u._id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  if (loading && users.length === 0) {
    return (
      <div className="nodes-system-workspace">
        <div className="loading-state">
          <RefreshCw size={24} className="spin" />
          <p>Loading users from database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nodes-system-workspace">
      
      {/* Notification */}
      {notification && (
        <div className={`notification-toast ${notification.type}`}>
          {notification.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* ===== MASTER MODULE HEADER ===== */}
      <header className="nodes-header">
        <div className="nodes-header-meta">
          <h1>EraX User Management</h1>
          <p>Supervise structural account profiles, wallet balance parameters, tracking metrics, and security authorizations.</p>
        </div>
        <div className="header-actions">
          <button 
            type="button" 
            className="create-btn primary"
            onClick={() => setShowCreateModal(true)}
          >
            <UserPlus size={16} />
            <span>Create User</span>
          </button>
          <button 
            type="button" 
            className="refresh-btn"
            onClick={() => fetchUsers()}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* ===== ACTIONS MATRIX TRACK STACK ===== */}
      <section className="nodes-control-bar">
        <div className="search-input-housing">
          <Search size={16} className="search-icon-fixed" />
          <input 
            type="text" 
            placeholder="Filter by name, email, or user ID..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="total-nodes-pill">
          <Users size={14} />
          <span>Total Users: {users.length}</span>
        </div>
      </section>

      {/* ===== METADATA DATABASE GRID LIST ===== */}
      <section className="nodes-matrix-wrapper">
        <div className="nodes-responsive-table">
          <table className="nodes-data-table">
            <thead>
              <tr>
                <th>Identity / Email</th>
                <th>Verification Status</th>
                <th>Available Balance</th>
                <th>Locked Investment</th>
                <th>Total Deposited</th>
                <th>Total Withdrawn</th>
                <th>Last Login</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user._id} className="node-data-row-item">
                  
                  {/* Identity profile cell */}
                  <td className="identity-profile-cell">
                    <div className="identity-avatar">{user.fullName?.charAt(0) || user.email?.charAt(0) || '?'}</div>
                    <div className="identity-text-stack">
                      <span className="id-core-name">{user.fullName || 'N/A'}</span>
                      <span className="id-sub-email">
                        <Mail size={10} style={{marginRight: '4px'}} />
                        {user.email}
                      </span>
                      <code className="mini-id-code">ID: {user._id?.slice(-8)}</code>
                    </div>
                  </td>

                  {/* Verification status */}
                  <td className="status-cell">
                    <span className={`status-badge ${user.isVerified ? 'verified' : 'pending'}`}>
                      {user.isVerified ? (
                        <><CheckCircle size={12} /> Verified</>
                      ) : (
                        <><AlertCircle size={12} /> Pending</>
                      )}
                    </span>
                    {user.isAdmin && (
                      <span className="admin-badge">
                        <Shield size={10} /> Admin
                      </span>
                    )}
                  </td>

                  {/* Available Balance */}
                  <td className="balance-cell">
                    <span className="balance-value">
                      {formatCurrency(user.balances?.availableLiquidity)}
                    </span>
                  </td>

                  {/* Locked Investment */}
                  <td className="balance-cell">
                    <span className="balance-value text-gold">
                      {formatCurrency(user.balances?.lockedInvestment)}
                    </span>
                  </td>

                  {/* Total Deposited */}
                  <td className="balance-cell">
                    <span className="balance-value text-green">
                      {formatCurrency(user.balances?.totalDeposited)}
                    </span>
                  </td>

                  {/* Total Withdrawn */}
                  <td className="balance-cell">
                    <span className="balance-value text-orange">
                      {formatCurrency(user.balances?.totalWithdrawn)}
                    </span>
                  </td>

                  {/* Last Login */}
                  <td className="timestamp-cell">
                    <div className="meta-line">
                      <Calendar size={14} />
                      <span>{formatDate(user.lastLoginAt)}</span>
                    </div>
                    <div className="meta-line text-slate offset-top">
                      <Clock size={14} />
                      <span>{formatTime(user.lastLoginAt)}</span>
                    </div>
                    {user.lastIp && (
                      <div className="meta-sub-hash">
                        <Globe size={10} />
                        <code>{user.lastIp}</code>
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="actions-button-cell text-center">
                    <div className="actions-button-cluster-row">
                      <button 
                        type="button" 
                        className="node-action-icon-btn view-accent"
                        onClick={() => handleViewUser(user)}
                        title="View complete profile"
                        disabled={actionLoading}
                      >
                        <Eye size={15} />
                      </button>
                      <button 
                        type="button" 
                        className="node-action-icon-btn edit-accent"
                        onClick={() => handleOpenEditModal(user)}
                        title="Edit user"
                        disabled={actionLoading}
                      >
                        <Edit3 size={15} />
                      </button>
                      <button 
                        type="button" 
                        className={`node-action-icon-btn ${user.isVerified ? 'unverify-accent' : 'verify-accent'}`}
                        onClick={() => handleToggleVerification(user._id, user.isVerified)}
                        title={user.isVerified ? 'Unverify user' : 'Verify user'}
                        disabled={actionLoading}
                      >
                        {user.isVerified ? <UserX size={15} /> : <UserCheck size={15} />}
                      </button>
                      <button 
                        type="button" 
                        className="node-action-icon-btn delete-accent"
                        onClick={() => handleDeleteUser(user._id)}
                        title="Delete user"
                        disabled={actionLoading}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>

                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="8" className="empty-search-notice">
                    {searchTerm ? 'No users found matching your search.' : 'No users in database yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ===== CREATE USER MODAL ===== */}
      {showCreateModal && (
        <div className="modal-backdrop-blur">
          <div className="modal-terminal-box">
            <div className="modal-terminal-header">
              <div className="title-cluster">
                <UserPlus size={18} className="text-gold" />
                <h3>Create New User</h3>
              </div>
              <button type="button" className="close-modal-btn" onClick={() => setShowCreateModal(false)}>
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="modal-terminal-content">
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input 
                    type="text"
                    value={createForm.fullName}
                    onChange={(e) => setCreateForm({...createForm, fullName: e.target.value})}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input 
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input 
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                    placeholder="Enter password (min 8 characters)"
                    required
                    minLength={8}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input 
                      type="checkbox"
                      checked={createForm.isAdmin}
                      onChange={(e) => setCreateForm({...createForm, isAdmin: e.target.checked})}
                    />
                    <span>Admin Access</span>
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input 
                      type="checkbox"
                      checked={createForm.isVerified}
                      onChange={(e) => setCreateForm({...createForm, isVerified: e.target.checked})}
                    />
                    <span>Verified Account</span>
                  </label>
                </div>
              </div>

              <div className="modal-terminal-actions">
                <button 
                  type="button" 
                  className="modal-cancel-btn" 
                  onClick={() => setShowCreateModal(false)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="modal-submit-btn primary"
                  disabled={actionLoading}
                >
                  {actionLoading ? <><RefreshCw size={16} className="spin" /> Creating...</> : <><Save size={16} /> Create User</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== EDIT USER MODAL ===== */}
      {showEditModal && selectedUser && (
        <div className="modal-backdrop-blur">
          <div className="modal-terminal-box large">
            <div className="modal-terminal-header">
              <div className="title-cluster">
                <Edit3 size={18} className="text-gold" />
                <h3>Edit User: <code className="accent-code-title">{selectedUser.email}</code></h3>
              </div>
              <button type="button" className="close-modal-btn" onClick={() => { setShowEditModal(false); setSelectedUser(null); }}>
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="modal-terminal-content">
              <div className="profile-sections-scroller">
                
                {/* Personal Information */}
                <div className="profile-section">
                  <h4 className="terminal-sub-heading">
                    <UserPlus size={14} /> Personal Information
                  </h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Full Name</label>
                      <input 
                        type="text"
                        value={editForm.fullName}
                        onChange={(e) => setEditForm({...editForm, fullName: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input 
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input 
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Location</label>
                      <input 
                        type="text"
                        value={editForm.location}
                        onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                        placeholder="Enter location"
                      />
                    </div>
                  </div>
                </div>

                {/* Account Settings */}
                <div className="profile-section">
                  <h4 className="terminal-sub-heading">
                    <Shield size={14} /> Account Settings
                  </h4>
                  <div className="form-grid">
                    <div className="form-group checkbox-group">
                      <label>
                        <input 
                          type="checkbox"
                          checked={editForm.isAdmin}
                          onChange={(e) => setEditForm({...editForm, isAdmin: e.target.checked})}
                        />
                        <span>Admin Access</span>
                      </label>
                    </div>
                    <div className="form-group checkbox-group">
                      <label>
                        <input 
                          type="checkbox"
                          checked={editForm.isVerified}
                          onChange={(e) => setEditForm({...editForm, isVerified: e.target.checked})}
                        />
                        <span>Verified Account</span>
                      </label>
                    </div>
                    <div className="form-group checkbox-group">
                      <label>
                        <input 
                          type="checkbox"
                          checked={editForm.twoStep}
                          onChange={(e) => setEditForm({...editForm, twoStep: e.target.checked})}
                        />
                        <span>2FA Enabled</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Financial Balances */}
                <div className="profile-section">
                  <h4 className="terminal-sub-heading">
                    <DollarSign size={14} /> Financial Balances
                  </h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Available Liquidity</label>
                      <div className="currency-input">
                        <span className="currency-symbol">$</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={editForm.balances.availableLiquidity}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            balances: {...editForm.balances, availableLiquidity: parseFloat(e.target.value) || 0}
                          })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Locked Investment</label>
                      <div className="currency-input">
                        <span className="currency-symbol">$</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={editForm.balances.lockedInvestment}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            balances: {...editForm.balances, lockedInvestment: parseFloat(e.target.value) || 0}
                          })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Total Deposited</label>
                      <div className="currency-input">
                        <span className="currency-symbol">$</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={editForm.balances.totalDeposited}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            balances: {...editForm.balances, totalDeposited: parseFloat(e.target.value) || 0}
                          })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Total Withdrawn</label>
                      <div className="currency-input">
                        <span className="currency-symbol">$</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={editForm.balances.totalWithdrawn}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            balances: {...editForm.balances, totalWithdrawn: parseFloat(e.target.value) || 0}
                          })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Net Profit/Loss</label>
                      <div className="currency-input">
                        <span className="currency-symbol">$</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={editForm.balances.netProfitLoss}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            balances: {...editForm.balances, netProfitLoss: parseFloat(e.target.value) || 0}
                          })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Total Invested</label>
                      <div className="currency-input">
                        <span className="currency-symbol">$</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={editForm.balances.totalInvested}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            balances: {...editForm.balances, totalInvested: parseFloat(e.target.value) || 0}
                          })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Current Investment Value</label>
                      <div className="currency-input">
                        <span className="currency-symbol">$</span>
                        <input 
                          type="number"
                          step="0.01"
                          value={editForm.balances.currentInvestmentValue}
                          onChange={(e) => setEditForm({
                            ...editForm, 
                            balances: {...editForm.balances, currentInvestmentValue: parseFloat(e.target.value) || 0}
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <div className="modal-terminal-actions">
                <button 
                  type="button" 
                  className="modal-cancel-btn" 
                  onClick={() => { setShowEditModal(false); setSelectedUser(null); }}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="modal-submit-btn primary"
                  disabled={actionLoading}
                >
                  {actionLoading ? <><RefreshCw size={16} className="spin" /> Updating...</> : <><Save size={16} /> Update User</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== VIEW USER DETAILS MODAL ===== */}
      {showViewModal && selectedUser && (
        <div className="modal-backdrop-blur">
          <div className="modal-terminal-box core-view-variant">
            <div className="modal-terminal-header">
              <div className="title-cluster">
                <Eye size={18} className="text-gold" />
                <h3>Complete User Profile: <code className="accent-code-title">{selectedUser.email}</code></h3>
              </div>
              <button type="button" className="close-modal-btn" onClick={() => { setShowViewModal(false); setSelectedUser(null); }}>
                <X size={18} />
              </button>
            </div>
            
            <div className="modal-terminal-content">
              <div className="profile-sections-scroller">
                
                {/* Personal Information */}
                <div className="profile-section">
                  <h4 className="terminal-sub-heading">
                    <UserPlus size={14} /> Personal Information
                  </h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Full Name</label>
                      <span>{selectedUser.fullName || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <span>{selectedUser.email}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone</label>
                      <span>{selectedUser.phone || 'Not provided'}</span>
                    </div>
                    <div className="info-item">
                      <label>User ID</label>
                      <code>{selectedUser._id}</code>
                    </div>
                    <div className="info-item">
                      <label>Joined Date</label>
                      <span>{formatDate(selectedUser.createdAt)}</span>
                    </div>
                    <div className="info-item">
                      <label>Auth Provider</label>
                      <span>{selectedUser.authProvider || 'email'}</span>
                    </div>
                    <div className="info-item">
                      <label>Referral Code</label>
                      <code>{selectedUser.referralCode || 'None'}</code>
                    </div>
                    <div className="info-item">
                      <label>2FA Enabled</label>
                      <span>{selectedUser.twoStep ? '✅ Yes' : '❌ No'}</span>
                    </div>
                  </div>
                </div>

                {/* Account Status */}
                <div className="profile-section">
                  <h4 className="terminal-sub-heading">
                    <Shield size={14} /> Account Status
                  </h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Verification Status</label>
                      <span className={`status-badge ${selectedUser.isVerified ? 'verified' : 'pending'}`}>
                        {selectedUser.isVerified ? 'Verified' : 'Pending'}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Admin Access</label>
                      <span>{selectedUser.isAdmin ? '✅ Yes' : '❌ No'}</span>
                    </div>
                    <div className="info-item">
                      <label>Last Login</label>
                      <span>{formatDate(selectedUser.lastLoginAt)} {formatTime(selectedUser.lastLoginAt)}</span>
                    </div>
                    <div className="info-item">
                      <label>Last IP Address</label>
                      <code>{selectedUser.lastIp || 'N/A'}</code>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="profile-section">
                  <h4 className="terminal-sub-heading">
                    <DollarSign size={14} /> Financial Summary
                  </h4>
                  <div className="financial-grid">
                    <div className="financial-card">
                      <label>Available Balance</label>
                      <span className="value gold">{formatCurrency(selectedUser.balances?.availableLiquidity)}</span>
                    </div>
                    <div className="financial-card">
                      <label>Locked Investment</label>
                      <span className="value gold">{formatCurrency(selectedUser.balances?.lockedInvestment)}</span>
                    </div>
                    <div className="financial-card">
                      <label>Total Deposited</label>
                      <span className="value green">{formatCurrency(selectedUser.balances?.totalDeposited)}</span>
                    </div>
                    <div className="financial-card">
                      <label>Total Withdrawn</label>
                      <span className="value orange">{formatCurrency(selectedUser.balances?.totalWithdrawn)}</span>
                    </div>
                    <div className="financial-card">
                      <label>Total Invested</label>
                      <span className="value">{formatCurrency(selectedUser.balances?.totalInvested)}</span>
                    </div>
                    <div className="financial-card">
                      <label>Current Investment Value</label>
                      <span className="value blue">{formatCurrency(selectedUser.balances?.currentInvestmentValue)}</span>
                    </div>
                    <div className="financial-card">
                      <label>Net Profit/Loss</label>
                      <span className={`value ${(selectedUser.balances?.netProfitLoss || 0) >= 0 ? 'green' : 'red'}`}>
                        {formatCurrency(selectedUser.balances?.netProfitLoss)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Portfolio Allocations */}
                {selectedUser.balances?.allocations && (
                  <div className="profile-section">
                    <h4 className="terminal-sub-heading">
                      <Activity size={14} /> Portfolio Allocations
                    </h4>
                    <div className="financial-grid">
                      <div className="financial-card">
                        <label>Stocks</label>
                        <span className="value">{formatCurrency(selectedUser.balances.allocations.stocks)}</span>
                      </div>
                      <div className="financial-card">
                        <label>Bonds</label>
                        <span className="value">{formatCurrency(selectedUser.balances.allocations.bonds)}</span>
                      </div>
                      <div className="financial-card">
                        <label>Commodities</label>
                        <span className="value">{formatCurrency(selectedUser.balances.allocations.commodities)}</span>
                      </div>
                    </div>
                  </div>
                )}

              </div>

              <div className="modal-terminal-actions">
                <button 
                  type="button" 
                  className="modal-cancel-btn" 
                  onClick={() => { setShowViewModal(false); setSelectedUser(null); }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}